import React from 'react';
import { PATENTS, SVG_COLORS } from '../data/constants.js';
import './patent-flywheel.css';

interface PatentFlywheelProps {
  selected: number | null;
  onSelect: (index: number | null) => void;
}

const CX = 260;
const CY = 260;
const R = 195;
const NW = 100;
const NH = 58;

export function PatentFlywheel({ selected, onSelect }: PatentFlywheelProps): React.ReactElement {
  const c = SVG_COLORS;

  return (
    <div className="mc-flywheel">
      <svg viewBox="0 0 520 520" className="mc-flywheel__svg">
        <defs>
          <radialGradient id="mc-centerGlow">
            <stop offset="0%" stopColor={c.accent} stopOpacity="0.2" />
            <stop offset="70%" stopColor={c.accent} stopOpacity="0.05" />
            <stop offset="100%" stopColor={c.accent} stopOpacity="0" />
          </radialGradient>
          <filter id="mc-glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Center glow */}
        <circle cx={CX} cy={CY} r="85" fill="url(#mc-centerGlow)" />

        {/* Center label */}
        <text
          x={CX}
          y={CY - 12}
          textAnchor="middle"
          fill={c.accent}
          fontSize="11"
          fontFamily="'JetBrains Mono', monospace"
          letterSpacing="2.5"
          fontWeight="600"
        >
          SELF-OPTIMIZING
        </text>
        <text
          x={CX}
          y={CY + 6}
          textAnchor="middle"
          fill={c.textBright}
          fontSize="13"
          fontFamily="'JetBrains Mono', monospace"
          letterSpacing="2"
        >
          DIGITAL OR
        </text>
        <text
          x={CX}
          y={CY + 22}
          textAnchor="middle"
          fill={c.textDim}
          fontSize="9"
          fontFamily="'JetBrains Mono', monospace"
          letterSpacing="1.5"
        >
          8-STEP FLYWHEEL
        </text>

        {/* Orbit ring */}
        <circle
          cx={CX}
          cy={CY}
          r={R}
          fill="none"
          stroke={c.accent}
          strokeOpacity="0.08"
          strokeWidth="1"
          strokeDasharray="5 5"
        />

        {/* Direction dots between nodes */}
        {PATENTS.map((_, i) => {
          const a1 = (i / 8) * Math.PI * 2 - Math.PI / 2;
          const a2 = ((i + 1) / 8) * Math.PI * 2 - Math.PI / 2;
          const midA = (a1 + a2) / 2;
          return (
            <circle
              key={`dot-${i}`}
              cx={CX + Math.cos(midA) * R}
              cy={CY + Math.sin(midA) * R}
              r="3"
              fill={c.accent}
              fillOpacity="0.25"
            />
          );
        })}

        {/* Patent nodes — rounded rectangles */}
        {PATENTS.map((p, i) => {
          const angle = (i / 8) * Math.PI * 2 - Math.PI / 2;
          const x = CX + Math.cos(angle) * R;
          const y = CY + Math.sin(angle) * R;
          const isSel = selected === i;
          const words = p.label.split(' ');
          const line1 = words.length <= 2 ? p.label : words.slice(0, 2).join(' ');
          const line2 = words.length > 2 ? words.slice(2).join(' ') : null;

          return (
            <g key={i} onClick={() => onSelect(isSel ? null : i)} className="mc-flywheel__node">
              {isSel && (
                <rect
                  x={x - NW / 2 - 6}
                  y={y - NH / 2 - 6}
                  width={NW + 12}
                  height={NH + 12}
                  rx="16"
                  fill={c.accent}
                  fillOpacity="0.08"
                />
              )}
              <rect
                x={x - NW / 2}
                y={y - NH / 2}
                width={NW}
                height={NH}
                rx="12"
                fill={isSel ? 'rgba(15,183,209,0.15)' : 'rgba(5,10,29,0.92)'}
                stroke={isSel ? c.accent : c.panelBorder}
                strokeWidth={isSel ? 2 : 1}
              />
              <text
                x={x}
                y={y - 12}
                textAnchor="middle"
                fill={isSel ? c.accent : c.textDim}
                fontSize="9"
                fontFamily="'JetBrains Mono', monospace"
                letterSpacing="1.5"
                fontWeight="500"
              >
                {`0${p.step}`}
              </text>
              <text
                x={x}
                y={y + 3}
                textAnchor="middle"
                fill={isSel ? c.textBright : c.text}
                fontSize="9"
                fontFamily="'JetBrains Mono', monospace"
                fontWeight="600"
              >
                {line1}
              </text>
              {line2 && (
                <text
                  x={x}
                  y={y + 15}
                  textAnchor="middle"
                  fill={isSel ? c.textBright : c.text}
                  fontSize="9"
                  fontFamily="'JetBrains Mono', monospace"
                  fontWeight="600"
                >
                  {line2}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ── Patent Detail Sub-component ───────────────────── */

interface PatentDetailProps {
  selectedIndex: number | null;
}

export function PatentDetail({ selectedIndex }: PatentDetailProps): React.ReactElement {
  if (selectedIndex === null) {
    return (
      <div className="mc-patent-detail">
        <div className="mc-patent-detail__placeholder">
          <span className="mc-patent-detail__placeholder-text">
            SELECT A NODE TO VIEW PATENT DETAILS
          </span>
        </div>
      </div>
    );
  }

  const patent = PATENTS[selectedIndex]!;
  return (
    <div className="mc-patent-detail mc-patent-detail--active">
      <div className="mc-patent-detail__step">
        STEP {patent.step} — {patent.label.toUpperCase()}
      </div>
      <div className="mc-patent-detail__id">{patent.id}</div>
      <div className="mc-patent-detail__desc">{patent.desc}</div>
      <div className="mc-patent-detail__text">{patent.detail}</div>
    </div>
  );
}
